import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { SessionProvider, useSession } from "./lib/session";
import { LoginPage } from "./pages/Login";
import { JoinPage } from "./pages/Join";
import { FieldInterviewerPage } from "./pages/FieldInterviewer";
import { Shell } from "./components/Shell";
import { EngagementsPage } from "./pages/Engagements";
import { EngagementPage } from "./pages/Engagement";
import { InterviewPage } from "./pages/Interview";
import { TeamPage } from "./pages/Team";
import { AdminAiPage } from "./pages/admin/AdminAi";
import { AdminClientPage, AdminClientsPage } from "./pages/admin/AdminClients";
import { AdminUsersPage } from "./pages/admin/AdminUsers";
import { AdminSecurityPage } from "./pages/admin/AdminSecurity";

function RequireAuth({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const loc = useLocation();
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return <>{children}</>;
}

/** The admin panel is for platform administrators only (the server enforces this too). */
function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const { me } = useSession();
  if (!me?.user.isPlatformAdmin)
    return (
      <div className="empty" style={{ marginTop: 80 }}>
        <h3>The admin panel is for platform administrators</h3>
        <p>Ask a Gramin Kriti platform administrator if you need a change.</p>
      </div>
    );
  return <>{children}</>;
}

function Home() {
  const { me } = useSession();
  if (!me) return <Navigate to="/login" replace />;
  const first = me.workspaces[0];
  if (first) return <Navigate to={`/w/${first.slug}`} replace />;
  if (me.user.isPlatformAdmin) return <Navigate to="/admin" replace />;
  return (
    <div className="empty" style={{ marginTop: 80 }}>
      <h3>You don't have access to a workspace yet</h3>
      <p>Ask your engagement lead to add {me.user.email} to their client workspace.</p>
    </div>
  );
}

export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* The interviewee's phone: no account, only the one-time pairing code. */}
        <Route path="/j/:code" element={<JoinPage />} />
        <Route
          path="/field/:tenant/:interviewId"
          element={
            <RequireAuth>
              <FieldInterviewerPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/*"
          element={
            <RequireAuth>
              <Shell>
                <RequirePlatformAdmin>
                  <Routes>
                    <Route index element={<Navigate to="ai" replace />} />
                    <Route path="ai" element={<AdminAiPage />} />
                    <Route path="clients" element={<AdminClientsPage />} />
                    <Route path="clients/:id" element={<AdminClientPage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="security" element={<AdminSecurityPage />} />
                  </Routes>
                </RequirePlatformAdmin>
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/w/:tenant/*"
          element={
            <RequireAuth>
              <Shell>
                <Routes>
                  <Route index element={<EngagementsPage />} />
                  <Route path="e/:eid/*" element={<EngagementPage />} />
                  <Route path="i/:id" element={<InterviewPage />} />
                  <Route path="team" element={<TeamPage />} />
                </Routes>
              </Shell>
            </RequireAuth>
          }
        />
        <Route path="*" element={<Home />} />
      </Routes>
    </SessionProvider>
  );
}
